import { Component, signal } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../core/services/auth.service';
import { ToastrService } from 'ngx-toastr';
import { Store } from '@ngrx/store';
import { FullscreenLoaderComponent } from '../../shared/components/loader-component/loader.component';
import * as PermissionActions from '../../core/permissions/permission.action';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

@Component({
  selector: 'app-login',
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    RouterModule,
    ReactiveFormsModule,
    CommonModule,
    FullscreenLoaderComponent,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent {
  loginForm: FormGroup;
  loading: boolean = false;
  hide = signal(true);
  permissionMapId: any;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private toastr: ToastrService,
    private store: Store
  ) {
    this.loginForm = this.fb.group({
      email: [
        '',
        [
          Validators.required,
          Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-z]{2,4}$/),
        ],
      ],
      password: ['', Validators.required],
      rememberMe: [false],
    });
  }

  ngOnInit(): void {
    this.autoLogin();
  }

  onEmailInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const lower = input.value.toLowerCase();
    if (input.value !== lower) {
      input.value = lower;
      this.loginForm.get('email')?.setValue(lower, { emitEvent: false });
    }
  }

  login() {
    const emailControl = this.loginForm.get('email');
    const passwordControl = this.loginForm.get('password');

    if (!emailControl?.value || !passwordControl?.value) {
      this.toastr.warning('Please fill in all fields');
      return;
    }

    if (emailControl.invalid) {
      this.toastr.warning('Please enter a valid email address');
      return;
    }

    if (this.loginForm.valid) {
      this.loading = true;
      const { email, password, rememberMe } = this.loginForm.value;

      this.authService.login(email, password).subscribe({
        next: (response) => {
          if (response.accessToken) {
            if (rememberMe) {
              sessionStorage.setItem('AUTH_TOKEN', response.accessToken);
              this.getProfile();
            } else {
              sessionStorage.setItem('AUTH_TOKEN', response.accessToken);
            }

            this.toastr.success('Login Successful');
            this.getProfile();
            this.authService.loadUserProfile();
            this.router.navigate(['/dashboard']);
          }
          this.loading = false;
        },
        error: (error) => {
          const statusCode = error.status;
          if (statusCode === 400) {
            console.log('Error:', error);
          }
          this.loading = false;
        },
      });
    }
  }

  clickEvent(event: MouseEvent) {
    this.hide.set(!this.hide());
    event.stopPropagation();
  }

  private autoLogin() {
    const token =
      sessionStorage.getItem('AUTH_TOKEN');
    if (token) {
      this.router.navigate(['/dashboard']);
    }
  }

  getProfile(): void {
    this.loading = true;
    this.authService.getUserProfile().subscribe({
      next: (res) => {
        const permissions = res?.data?.permissions;
        this.store.dispatch(PermissionActions.setPermissions({ permissions }));
        sessionStorage.setItem('roleID', res?.data?.admin?.role?.id);
        sessionStorage.setItem('userName', res?.data?.admin?.name);
        sessionStorage.setItem('userRole', res?.data?.admin?.role?.name);
        this.loading = false;
      },
      error: (err) => {
        console.error('Failed to load profile', err);
        this.loading = false;
      },
    });
  }
}
